import { Trans, useLingui } from '@lingui/react/macro';

import {
  type ActionTypeOrganizationAdditionalInfoConfig,
  type AdditionalInfoConfigElement,
  type AdditionalInfoConfigNumber,
  type AdditionalInfoConfigSelect,
  type AdditionalInfoConfigText,
  type AdditionalInfoConfigTextArea,
} from '@/api';
import Box from '@/design_system/Box';
import Button from '@/design_system/Button';
import FoldableSection from '@/design_system/FoldableSection';
import { InputSelect } from '@/design_system/InputSelect/InputSelect';
import { Label } from '@/design_system/Label/Label';
import { RadioItemYesNo } from '@/design_system/Radio/RadioItem/RadioItem';
import Stack from '@/design_system/Stack';
import Paragraph from '@/design_system/Typography/Paragraph';
import IconAdd from '@/icons/Add.svg';
import IconTrash from '@/icons/Trash.svg';

import { LocaleInputs } from '../../shared/LocaleInputs';

export const AdditionalInfoConfigInputs = ({
  additionalInfoConfig,
  setAdditionalInfoConfig,
  showErrors,
}: {
  additionalInfoConfig: ActionTypeOrganizationAdditionalInfoConfig;
  setAdditionalInfoConfig: (
    additionalInfoConfig: ActionTypeOrganizationAdditionalInfoConfig
  ) => void;
  showErrors: boolean;
}) => {
  const handleUpdateAdditionalInfoConfig = (
    updatedAdditionalInfoConfigElement: AdditionalInfoConfigElement
  ) => {
    setAdditionalInfoConfig(
      additionalInfoConfig.map((element) =>
        updatedAdditionalInfoConfigElement.id === element.id
          ? updatedAdditionalInfoConfigElement
          : element
      )
    );
  };

  const handleDeleteAdditionalInfoConfigElement = (
    additionalInfoConfigElementToDelete: AdditionalInfoConfigElement
  ) => {
    setAdditionalInfoConfig(
      additionalInfoConfig.filter(
        (element) => additionalInfoConfigElementToDelete.id !== element.id
      )
    );
  };

  return (
    <Stack gap="0.5rem">
      <p className="label-100-medium">
        <Trans id="settings.actions.actions.drawer.additional-info-config">
          Additional information
        </Trans>
      </p>
      {additionalInfoConfig.map((additionalInfoConfigElement) => {
        return (
          <Box key={additionalInfoConfigElement.id}>
            <Stack gap="0.5rem">
              <TypeInput
                currentConfigElement={additionalInfoConfigElement}
                handleUpdateAdditionalInfoConfig={handleUpdateAdditionalInfoConfig}
                handleDeleteAdditionalInfoConfigElement={handleDeleteAdditionalInfoConfigElement}
              />

              {additionalInfoConfigElement.type === 'text' && (
                <TextInputs
                  additionalInfoConfigElement={additionalInfoConfigElement}
                  handleUpdateAdditionalInfoConfig={handleUpdateAdditionalInfoConfig}
                  showErrors={showErrors}
                />
              )}

              {additionalInfoConfigElement.type === 'text-area' && (
                <TextAreaInputs
                  additionalInfoConfigElement={additionalInfoConfigElement}
                  handleUpdateAdditionalInfoConfig={handleUpdateAdditionalInfoConfig}
                  showErrors={showErrors}
                />
              )}

              {additionalInfoConfigElement.type === 'select' && (
                <SelectInputs
                  additionalInfoConfigElement={additionalInfoConfigElement}
                  handleUpdateAdditionalInfoConfig={handleUpdateAdditionalInfoConfig}
                  showErrors={showErrors}
                />
              )}

              {additionalInfoConfigElement.type === 'number' && (
                <NumberInputs
                  additionalInfoConfigElement={additionalInfoConfigElement}
                  handleUpdateAdditionalInfoConfig={handleUpdateAdditionalInfoConfig}
                  showErrors={showErrors}
                />
              )}

              <IsEnabledInput
                additionalInfoConfigElement={additionalInfoConfigElement}
                handleUpdateAdditionalInfoConfig={handleUpdateAdditionalInfoConfig}
              />

              <IsRequiredInput
                additionalInfoConfigElement={additionalInfoConfigElement}
                handleUpdateAdditionalInfoConfig={handleUpdateAdditionalInfoConfig}
              />
            </Stack>
          </Box>
        );
      })}
      <AddConfigurationButton
        additionalInfoConfig={additionalInfoConfig}
        setAdditionalInfoConfig={setAdditionalInfoConfig}
      />
    </Stack>
  );
};

const AddConfigurationButton = ({
  additionalInfoConfig,
  setAdditionalInfoConfig,
}: {
  additionalInfoConfig: ActionTypeOrganizationAdditionalInfoConfig;
  setAdditionalInfoConfig: (
    additionalInfoConfig: ActionTypeOrganizationAdditionalInfoConfig
  ) => void;
}) => {
  return (
    <div>
      <Button
        variant="link-subtle"
        size="small"
        onPress={() => {
          setAdditionalInfoConfig([
            ...additionalInfoConfig,
            {
              id: crypto.randomUUID(),
              type: 'text',
              enabled: true,
              required: true,
              config: {
                label: { en: '', fr: '' },
                placeholder: { en: '', fr: '' },
              },
            },
          ]);
        }}
      >
        <IconAdd />
        <Trans id="settings.actions.actions.drawer.additional-info-config.add">
          Add configuration
        </Trans>
      </Button>
    </div>
  );
};

const DeleteConfigurationButton = ({
  additionalInfoConfigElement,
  handleDeleteAdditionalInfoConfigElement,
}: {
  additionalInfoConfigElement: AdditionalInfoConfigElement;
  handleDeleteAdditionalInfoConfigElement: (
    additionalInfoConfigElement: AdditionalInfoConfigElement
  ) => void;
}) => {
  const { t } = useLingui();

  return (
    <Button
      ariaLabel={t({
        id: 'settings.actions.actions.drawer.additional-info-config.delete',
        message: 'Delete',
      })}
      tooltip={t({
        id: 'settings.actions.actions.drawer.additional-info-config.delete',
        message: 'Delete',
      })}
      iconOnly
      size="large"
      variant="secondary"
      onPress={() => handleDeleteAdditionalInfoConfigElement(additionalInfoConfigElement)}
      style={{ alignSelf: 'end' }}
    >
      <IconTrash />
    </Button>
  );
};

const DeleteSelectOptionButton = ({
  option,
  additionalInfoConfigElement,
  handleUpdateAdditionalInfoConfig,
}: {
  option: AdditionalInfoConfigSelect['config']['options'][number];
  additionalInfoConfigElement: AdditionalInfoConfigSelect;
  handleUpdateAdditionalInfoConfig: (
    additionalInfoConfigElement: AdditionalInfoConfigElement
  ) => void;
}) => {
  const { t } = useLingui();

  return (
    <Button
      ariaLabel={t({
        id: 'settings.actions.actions.drawer.additional-info-config.select.option.delete',
        message: 'Delete option',
      })}
      tooltip={t({
        id: 'settings.actions.actions.drawer.additional-info-config.select.option.delete',
        message: 'Delete option',
      })}
      iconOnly
      size="small"
      variant="secondary"
      onPress={() =>
        handleUpdateAdditionalInfoConfig({
          ...additionalInfoConfigElement,
          config: {
            ...additionalInfoConfigElement.config,
            options: additionalInfoConfigElement.config.options.filter(
              (elementOption) => option.id !== elementOption.id
            ),
          },
        })
      }
    >
      <IconTrash />
    </Button>
  );
};

/**
 *
 * All inputs specific to Text type
 */
const TextInputs = ({
  additionalInfoConfigElement,
  handleUpdateAdditionalInfoConfig,
  showErrors,
}: {
  additionalInfoConfigElement: AdditionalInfoConfigText;
  handleUpdateAdditionalInfoConfig: (
    additionalInfoConfigElement: AdditionalInfoConfigElement
  ) => void;
  showErrors: boolean;
}) => {
  const { t } = useLingui();

  return (
    <>
      <LocaleInputs
        label={t({
          id: 'settings.actions.actions.drawer.additional-info-config.text.label',
          message: 'Label',
        })}
        placeholder={t({
          id: 'settings.actions.actions.drawer.additional-info-config.text.label.placeholder',
          message: 'i.e: Engraving text',
        })}
        value={additionalInfoConfigElement.config.label}
        setValue={(newLabel) =>
          handleUpdateAdditionalInfoConfig({
            ...additionalInfoConfigElement,
            config: {
              ...additionalInfoConfigElement.config,
              label: newLabel,
            },
          })
        }
        showErrors={showErrors}
      />

      <LocaleInputs
        label={t({
          id: 'settings.actions.actions.drawer.additional-info-config.text.placeholder.label',
          message: 'Placeholder',
        })}
        placeholder={t({
          id: 'settings.actions.actions.drawer.additional-info-config.text.placeholder.placeholder',
          message: 'i.e: Please enter an engraving text',
        })}
        value={additionalInfoConfigElement.config.placeholder}
        setValue={(newPlaceholder) =>
          handleUpdateAdditionalInfoConfig({
            ...additionalInfoConfigElement,
            config: {
              ...additionalInfoConfigElement.config,
              placeholder: newPlaceholder,
            },
          })
        }
        showErrors={showErrors}
      />
    </>
  );
};

/**
 *
 * All inputs specific to Text area type
 */
const TextAreaInputs = ({
  additionalInfoConfigElement,
  handleUpdateAdditionalInfoConfig,
  showErrors,
}: {
  additionalInfoConfigElement: AdditionalInfoConfigTextArea;
  handleUpdateAdditionalInfoConfig: (
    additionalInfoConfigElement: AdditionalInfoConfigElement
  ) => void;
  showErrors: boolean;
}) => {
  const { t } = useLingui();

  return (
    <>
      <LocaleInputs
        label={t({
          id: 'settings.actions.actions.drawer.additional-info-config.text-area.label',
          message: 'Label',
        })}
        placeholder={t({
          id: 'settings.actions.actions.drawer.additional-info-config.text-area.label.placeholder',
          message: 'i.e: Details',
        })}
        value={additionalInfoConfigElement.config.label}
        setValue={(newLabel) => {
          handleUpdateAdditionalInfoConfig({
            ...additionalInfoConfigElement,
            config: {
              ...additionalInfoConfigElement.config,
              label: newLabel,
            },
          });
        }}
        showErrors={showErrors}
      />

      <LocaleInputs
        label={t({
          id: 'settings.actions.actions.drawer.additional-info-config.text-area.placeholder.label',
          message: 'Placeholder',
        })}
        placeholder={t({
          id: 'settings.actions.actions.drawer.additional-info-config.text-area.placeholder.placeholder',
          message: 'i.e: Add details...',
        })}
        value={additionalInfoConfigElement.config.placeholder}
        setValue={(newPlaceholder) => {
          handleUpdateAdditionalInfoConfig({
            ...additionalInfoConfigElement,
            config: {
              ...additionalInfoConfigElement.config,
              placeholder: newPlaceholder,
            },
          });
        }}
        showErrors={showErrors}
      />
    </>
  );
};

/**
 *
 * All inputs specific to Select type
 */
const SelectInputs = ({
  additionalInfoConfigElement,
  handleUpdateAdditionalInfoConfig,
  showErrors,
}: {
  additionalInfoConfigElement: AdditionalInfoConfigSelect;
  handleUpdateAdditionalInfoConfig: (
    additionalInfoConfigElement: AdditionalInfoConfigElement
  ) => void;
  showErrors: boolean;
}) => {
  const { t } = useLingui();

  return (
    <>
      <LocaleInputs
        label={t({
          id: 'settings.actions.actions.drawer.additional-info-config.select.label',
          message: 'Label',
        })}
        placeholder={t({
          id: 'settings.actions.actions.drawer.additional-info-config.select.label.placeholder',
          message: 'i.e: Engraving style',
        })}
        value={additionalInfoConfigElement.config.label}
        setValue={(newLabel) =>
          handleUpdateAdditionalInfoConfig({
            ...additionalInfoConfigElement,
            config: {
              ...additionalInfoConfigElement.config,
              label: newLabel,
            },
          })
        }
        showErrors={showErrors}
      />

      <LocaleInputs
        label={t({
          id: 'settings.actions.actions.drawer.additional-info-config.select.placeholder.label',
          message: 'Placeholder',
        })}
        placeholder={t({
          id: 'settings.actions.actions.drawer.additional-info-config.select.placeholder.placeholder',
          message: 'i.e: Select an option...',
        })}
        value={additionalInfoConfigElement.config.placeholder}
        setValue={(newPlaceholder) => {
          handleUpdateAdditionalInfoConfig({
            ...additionalInfoConfigElement,
            config: {
              ...additionalInfoConfigElement.config,
              placeholder: newPlaceholder,
            },
          });
        }}
        showErrors={showErrors}
      />

      {!additionalInfoConfigElement.config.options.length ? (
        <div>
          <Label
            label={t({
              id: 'settings.actions.actions.drawer.additional-info-config.select.options',
              message: 'Options',
            })}
          />
          <Paragraph color="secondary">
            <Trans id="settings.actions.actions.drawer.additional-info-config.select.options.no-options">
              No options added yet
            </Trans>
          </Paragraph>
          <AddSelectOptionButton
            additionalInfoConfigElement={additionalInfoConfigElement}
            handleUpdateAdditionalInfoConfig={handleUpdateAdditionalInfoConfig}
          />
        </div>
      ) : (
        <SelectOptions
          key={additionalInfoConfigElement.id}
          additionalInfoConfigElement={additionalInfoConfigElement}
          handleUpdateAdditionalInfoConfig={handleUpdateAdditionalInfoConfig}
          showErrors={showErrors}
        />
      )}
    </>
  );
};

const SelectOptions = ({
  additionalInfoConfigElement,
  handleUpdateAdditionalInfoConfig,
  showErrors,
}: {
  additionalInfoConfigElement: AdditionalInfoConfigSelect;
  handleUpdateAdditionalInfoConfig: (
    additionalInfoConfigElement: AdditionalInfoConfigElement
  ) => void;
  showErrors: boolean;
}) => {
  const { t } = useLingui();

  return (
    <FoldableSection
      label={t({
        id: 'settings.actions.actions.drawer.additional-info-config.select.options',
        message: 'Options',
      })}
    >
      <Stack gap="0.5rem">
        {additionalInfoConfigElement.config.options.map((option) => (
          <SelectOption
            key={option.id}
            option={option}
            additionalInfoConfigElement={additionalInfoConfigElement}
            handleUpdateAdditionalInfoConfig={handleUpdateAdditionalInfoConfig}
            showErrors={showErrors}
          />
        ))}
        <AddSelectOptionButton
          additionalInfoConfigElement={additionalInfoConfigElement}
          handleUpdateAdditionalInfoConfig={handleUpdateAdditionalInfoConfig}
        />
      </Stack>
    </FoldableSection>
  );
};

const SelectOption = ({
  option,
  additionalInfoConfigElement,
  handleUpdateAdditionalInfoConfig,
  showErrors,
}: {
  option: AdditionalInfoConfigSelect['config']['options'][number];
  additionalInfoConfigElement: AdditionalInfoConfigSelect;
  handleUpdateAdditionalInfoConfig: (
    additionalInfoConfigElement: AdditionalInfoConfigElement
  ) => void;
  showErrors: boolean;
}) => {
  const { t } = useLingui();

  const optionNumber =
    additionalInfoConfigElement.config.options.findIndex(
      (elementOption) => option.id === elementOption.id
    ) + 1;

  return (
    <Box gap="0.5rem">
      <LocaleInputs
        label={t({
          id: 'settings.actions.actions.drawer.additional-info-config.select.options.option.label',
          message: `Option n°${optionNumber} label`,
        })}
        placeholder={t({
          id: 'settings.actions.actions.drawer.additional-info-config.select.options.option.placeholder',
          message: 'i.e: Classic font',
        })}
        value={option.label}
        setValue={(newOptionLabel) =>
          handleUpdateAdditionalInfoConfig({
            ...additionalInfoConfigElement,
            config: {
              ...additionalInfoConfigElement.config,
              options: additionalInfoConfigElement.config.options.map((elementOption) => {
                const isCurrentOption = option.id === elementOption.id;

                if (isCurrentOption) {
                  return { ...elementOption, label: newOptionLabel };
                } else {
                  return elementOption;
                }
              }),
            },
          })
        }
        showErrors={showErrors}
      />

      <DeleteSelectOptionButton
        option={option}
        additionalInfoConfigElement={additionalInfoConfigElement}
        handleUpdateAdditionalInfoConfig={handleUpdateAdditionalInfoConfig}
      />
    </Box>
  );
};

const AddSelectOptionButton = ({
  additionalInfoConfigElement,
  handleUpdateAdditionalInfoConfig,
}: {
  additionalInfoConfigElement: AdditionalInfoConfigSelect;
  handleUpdateAdditionalInfoConfig: (
    additionalInfoConfigElement: AdditionalInfoConfigElement
  ) => void;
}) => {
  return (
    <div>
      <Button
        variant="link-subtle"
        size="small"
        onPress={() =>
          handleUpdateAdditionalInfoConfig({
            ...additionalInfoConfigElement,
            config: {
              ...additionalInfoConfigElement.config,
              options: [
                ...additionalInfoConfigElement.config.options,
                {
                  id: crypto.randomUUID(),
                  label: { en: '', fr: '' },
                },
              ],
            },
          })
        }
      >
        <IconAdd />
        <Trans id="settings.actions.actions.drawer.additional-info-config.select.add-option">
          Add an option
        </Trans>
      </Button>
    </div>
  );
};

/**
 *
 * All inputs specific to Number type
 */
const NumberInputs = ({
  additionalInfoConfigElement,
  handleUpdateAdditionalInfoConfig,
  showErrors,
}: {
  additionalInfoConfigElement: AdditionalInfoConfigNumber;
  handleUpdateAdditionalInfoConfig: (
    additionalInfoConfigElement: AdditionalInfoConfigElement
  ) => void;
  showErrors: boolean;
}) => {
  const { t } = useLingui();

  return (
    <LocaleInputs
      label={t({
        id: 'settings.actions.actions.drawer.additional-info-config.number.label.label',
        message: 'Label',
      })}
      placeholder={t({
        id: 'settings.actions.actions.drawer.additional-info-config.number.label.placeholder',
        message: 'i.e: Current clasp earring height (in cm)',
      })}
      value={additionalInfoConfigElement.config.label}
      setValue={(newLabel) =>
        handleUpdateAdditionalInfoConfig({
          ...additionalInfoConfigElement,
          config: {
            ...additionalInfoConfigElement.config,
            label: newLabel,
          },
        })
      }
      showErrors={showErrors}
    />
  );
};

const TypeInput = ({
  currentConfigElement,
  handleUpdateAdditionalInfoConfig,
  handleDeleteAdditionalInfoConfigElement,
}: {
  currentConfigElement: AdditionalInfoConfigElement;
  handleUpdateAdditionalInfoConfig: (
    additionalInfoConfigElement: AdditionalInfoConfigElement
  ) => void;
  handleDeleteAdditionalInfoConfigElement: (
    additionalInfoConfigElement: AdditionalInfoConfigElement
  ) => void;
}) => {
  const { t } = useLingui();

  const additionalDataConfigTypeOptions = [
    {
      id: 'text',
      text: t({
        id: 'settings.actions.actions.drawer.additional-info-config.type.text',
        message: 'Text',
      }),
    },
    {
      id: 'text-area',
      text: t({
        id: 'settings.actions.actions.drawer.additional-info-config.type.text-area',
        message: 'Text area',
      }),
    },
    {
      id: 'select',
      text: t({
        id: 'settings.actions.actions.drawer.additional-info-config.type.select',
        message: 'Select',
      }),
    },
    {
      id: 'number',
      text: t({
        id: 'settings.actions.actions.drawer.additional-info-config.type.number',
        message: 'Number',
      }),
    },
  ];

  return (
    <Stack row gap="0.5rem">
      <InputSelect
        style={{ flex: 1 }}
        options={additionalDataConfigTypeOptions}
        value={additionalDataConfigTypeOptions.find(({ id }) => id === currentConfigElement.type)}
        label={t({
          id: 'settings.actions.actions.drawer.additional-info-config.type',
          message: 'Type',
        })}
        getOptionValue={(option) => option.id}
        getOptionLabel={(option) => option.text}
        onChange={(option) => {
          if (!option) {
            return;
          }

          if (option.id === 'text') {
            handleUpdateAdditionalInfoConfig({
              id: currentConfigElement.id,
              type: 'text',
              enabled: currentConfigElement.enabled,
              required: currentConfigElement.required,
              config: {
                label: { en: '', fr: '' },
                placeholder: { en: '', fr: '' },
              },
            } satisfies AdditionalInfoConfigText);
          } else if (option.id === 'text-area') {
            handleUpdateAdditionalInfoConfig({
              id: currentConfigElement.id,
              type: 'text-area',
              enabled: currentConfigElement.enabled,
              required: currentConfigElement.required,
              config: {
                label: { en: '', fr: '' },
                placeholder: { en: '', fr: '' },
              },
            } satisfies AdditionalInfoConfigTextArea);
          } else if (option.id === 'select') {
            handleUpdateAdditionalInfoConfig({
              id: currentConfigElement.id,
              type: 'select',
              enabled: currentConfigElement.enabled,
              required: currentConfigElement.required,
              config: {
                label: { en: '', fr: '' },
                placeholder: { en: '', fr: '' },
                options: [],
              },
            } satisfies AdditionalInfoConfigSelect);
          } else if (option.id === 'number') {
            handleUpdateAdditionalInfoConfig({
              id: currentConfigElement.id,
              type: 'number',
              enabled: currentConfigElement.enabled,
              required: currentConfigElement.required,
              config: {
                label: { en: '', fr: '' },
              },
            } satisfies AdditionalInfoConfigNumber);
          }
        }}
      />
      <DeleteConfigurationButton
        additionalInfoConfigElement={currentConfigElement}
        handleDeleteAdditionalInfoConfigElement={handleDeleteAdditionalInfoConfigElement}
      />
    </Stack>
  );
};

const IsEnabledInput = ({
  additionalInfoConfigElement,
  handleUpdateAdditionalInfoConfig,
}: {
  additionalInfoConfigElement: AdditionalInfoConfigElement;
  handleUpdateAdditionalInfoConfig: (
    additionalInfoConfigElement: AdditionalInfoConfigElement
  ) => void;
}) => {
  const { t } = useLingui();

  return (
    <Label
      label={t({
        id: 'settings.actions.actions.drawer.additional-info-config.is-enabled',
        message: 'Enabled',
      })}
    >
      <RadioItemYesNo
        name="isEnabled"
        value={additionalInfoConfigElement.enabled ? 'yes' : 'no'}
        onChange={(newIsEnabled) =>
          handleUpdateAdditionalInfoConfig({
            ...additionalInfoConfigElement,
            enabled: newIsEnabled === 'yes',
          })
        }
      />
    </Label>
  );
};

const IsRequiredInput = ({
  additionalInfoConfigElement,
  handleUpdateAdditionalInfoConfig,
}: {
  additionalInfoConfigElement: AdditionalInfoConfigElement;
  handleUpdateAdditionalInfoConfig: (
    additionalInfoConfigElement: AdditionalInfoConfigElement
  ) => void;
}) => {
  const { t } = useLingui();

  return (
    <Label
      label={t({
        id: 'settings.actions.actions.drawer.additional-info-config.is-required',
        message: 'Required',
      })}
    >
      <RadioItemYesNo
        name="isRequired"
        value={additionalInfoConfigElement.required ? 'yes' : 'no'}
        onChange={(newIsRequired) =>
          handleUpdateAdditionalInfoConfig({
            ...additionalInfoConfigElement,
            required: newIsRequired === 'yes',
          })
        }
      />
    </Label>
  );
};
